import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export type Theme = 'light' | 'dark' | 'system'

export const useThemeStore = defineStore('theme', () => {
  // State
  const theme = ref<Theme>('system')
  const isDark = ref(false)

  // Computed
  const currentTheme = computed(() => {
    if (theme.value === 'system') {
      return isDark.value ? 'dark' : 'light'
    }
    return theme.value
  })

  const themeIcon = computed(() => {
    return currentTheme.value === 'dark' ? 'moon' : 'sun'
  })

  // Methods
  const setTheme = (newTheme: Theme) => {
    theme.value = newTheme
    applyTheme()
  }

  const toggleTheme = () => {
    if (theme.value === 'light') {
      setTheme('dark')
    } else if (theme.value === 'dark') {
      setTheme('light')
    } else {
      // If system, toggle between light and dark
      setTheme(isDark.value ? 'light' : 'dark')
    }
  }

  const applyTheme = () => {
    const html = document.documentElement

    if (currentTheme.value === 'dark') {
      html.classList.add('dark')
      html.classList.remove('light')
    } else {
      html.classList.remove('dark')
      html.classList.add('light')
    }

    // Save to localStorage
    localStorage.setItem('theme', theme.value)
  }

  const detectSystemTheme = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    isDark.value = mediaQuery.matches

    // Listen for system theme changes
    mediaQuery.addEventListener('change', e => {
      isDark.value = e.matches
      if (theme.value === 'system') {
        applyTheme()
      }
    })
  }

  const initializeTheme = () => {
    try {
      // Load saved theme preference
      const savedTheme = localStorage.getItem('theme') as Theme
      
      if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
        theme.value = savedTheme
      }

      // Detect system theme
      detectSystemTheme()

      // Apply theme
      applyTheme()
    } catch (error) {
      console.error('Theme store: Error during theme initialization:', error)
    }
  }

  // Watch for theme changes
  watch(theme, () => {
    applyTheme()
  })

  return {
    // State
    theme,
    isDark,

    // Computed
    currentTheme,
    themeIcon,

    // Methods
    setTheme,
    toggleTheme,
    applyTheme,
    detectSystemTheme,
    initializeTheme,
  }
})
